import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// DeclineFutureNeedDialog — Step 3 (Follow-up).
// Captures a required reason and calls public.decline_treatment_need.
//
// Quick-pick reasons act as one-tap shortcuts; the textarea is always
// available for free-text. Either path produces the same RPC call.
// =====================================================================

const QUICK_REASONS = [
  "Patient not interested",
  "Cost concern",
  "Will think about it",
  "Going to another clinic",
  "No longer needed",
];

export default function DeclineFutureNeedDialog({
  open,
  onOpenChange,
  bundle,         // { procedure_name, tooth_number, surfaces: [...], needs: [...] }
                  // Same tooth + same procedure = one bundle. The shared
                  // reason gets applied to every surface in the bundle.
  patientName,
  onDeclined,     // (needIds) => void — refresh callback
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setReason(""); setSubmitting(false); }
  }, [open]);

  const close = () => { if (!submitting) onOpenChange(false); };
  const trimmed = reason.trim();
  const targets = bundle?.needs ?? [];
  const canSubmit = trimmed.length > 0 && !submitting && targets.length > 0;

  // Stage-only — no DB write here. The atomic commit fires from Step 3's
  // "Complete checkout" button via commit_checkout_with_followup. We just
  // hand the typed reason back up; the parent stages it.
  function submit() {
    if (!canSubmit) return;
    onDeclined?.(trimmed);
    onOpenChange(false);
  }

  if (!open) return null;
  const isAll = !!bundle?.isAll;
  const heading = isAll ? `Decline all ${bundle.needs?.length ?? 0} future needs` : "Decline future need";
  const subtitle = bundle
    ? isAll
      ? `${bundle.needs?.length ?? 0} surfaces — same reason applied to all`
      : (() => {
          const tooth = bundle.tooth_number ? ` · #${bundle.tooth_number}` : "";
          const surf  = (bundle.surfaces ?? []).filter(Boolean);
          const surfStr = surf.length > 0 ? ` (${surf.join(", ")})` : "";
          return `${bundle.procedure_name}${tooth}${surfStr}`;
        })()
    : "";

  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="decline-future-need-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900">{heading}</h2>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              <span className="text-slate-700 font-medium">{patientName}</span>
              {subtitle ? <> · {subtitle}</> : null}
            </p>
          </div>
          <button type="button" onClick={close} disabled={submitting}
            data-testid="decline-future-need-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-900 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>Declining is permanent on this need. The status moves to <strong>Declined</strong> and won't appear on future visits unless re-added.</span>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Quick reasons</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {QUICK_REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  data-testid={`decline-quick-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-xs px-2 py-1 rounded-full border ${
                    reason === r
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  }`}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="decline-reason" className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              Reason <span className="text-rose-600 normal-case font-medium">(required)</span>
            </label>
            <textarea id="decline-reason" rows={3}
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Add detail (or pick a quick reason above)…"
              data-testid="decline-future-need-reason"
              className="mt-1 w-full px-2.5 py-2 text-sm rounded border border-slate-200 focus:border-rose-500 outline-none resize-none" />
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <button type="button" onClick={close} disabled={submitting}
            data-testid="decline-future-need-cancel"
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded disabled:opacity-40">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit}
            data-testid="decline-future-need-submit"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded disabled:opacity-50">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}
